/*
 * Hey Coasters Studio: one path for custom artwork orders.
 * Shape, file, preview, quantity, price and approval in one form. The button reflects
 * what is still missing and only adds to the cart when a file is uploaded and approved.
 * Print file: 99 x 99 mm artboard (93 mm trim + 3 mm bleed) at 300 DPI, 1170 x 1170 px.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

// Opens Horizon's cart drawer (<theme-drawer id="cart-drawer">) after a successful add.
// The cart-items-component already re-renders from the `sections` attached to the event promise.
function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open();
}

const TRIM_MM = 93;
const BLEED_MM = 3;
const SAFE_MM = 4;
const SQUARE_CORNER_RATIO = 0.06;
const DPI = 300;
const SIZE = Math.ceil(((TRIM_MM + 2 * BLEED_MM) / 25.4) * DPI);
const PX_PER_MM = SIZE / (TRIM_MM + 2 * BLEED_MM);
const MAX_BYTES = 20 * 1024 * 1024;
const PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.min.mjs';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs';

/* Texts and canvas colours come from the section settings (data-st-config). These are the fallbacks. */
const DEFAULT_TEXT = {
  property_artwork: 'Artwork',
  property_notes: 'Notes',
  canvas_placeholder: 'Your artwork appears here',
  canvas_pdf_placeholder: 'PDF received, preview not available',
  file_cta: 'Choose your file',
  file_cta_replace: 'Replace file',
  res_waiting: 'Waiting for your file',
  res_pdf_manual: 'PDF received. Our team will check it before print.',
  res_pdf_ok: 'PDF rendered at 300 DPI',
  res_good: 'About {dpi} DPI, print ready',
  res_ok: 'About {dpi} DPI, usable. 300 DPI is sharper.',
  res_low: 'About {dpi} DPI, too low to print sharply',
  selection: '{count} {shape} coasters',
  pack_each: '{price} each',
  pack_save: 'Save {saving}%',
  btn_unavailable: 'Unavailable',
  next_unavailable: 'This option is not available right now.',
  btn_busy: 'Adding to cart…',
  next_busy: 'Preparing your print file.',
  btn_reading: 'Reading your file…',
  next_reading: 'This takes a few seconds.',
  btn_upload: 'Upload your artwork',
  next_upload: 'Next: upload your artwork.',
  btn_sharper: 'Choose a sharper file',
  next_sharper: 'Your file is below 150 DPI at this size. Upload a larger file or reduce the zoom.',
  btn_approve: 'Approve your artwork',
  next_approve: 'Next: check the preview and tick the approval box.',
  btn_add: 'Add {count} custom coasters',
  next_ready: '{price} for {count} {shape} coasters. Ships within 5 working days.',
  status_reading: 'Reading {file}…',
  status_too_large: 'This file is larger than 20 MB. Please export a smaller file.',
  status_wrong_type: 'Please upload a PDF, JPG or PNG file.',
  status_pdf_no_preview: '{file} received. The preview is not available, our team will check it manually.',
  status_unreadable: 'We could not read this file. Please try a PDF, JPG or PNG.',
  error_add: 'We could not add this item. Please try again.',
};
const DEFAULT_COLORS = {
  cut: '#FF8F1C',
  safe: 'rgba(20,28,40,.55)',
  outside: 'rgba(247,248,250,.72)',
  placeholderBg: '#EEF1F4',
  placeholderText: 'rgba(20,28,40,.45)',
};

/* Keeps the defaults and takes every non-empty string from the settings. */
const withOverrides = (defaults, overrides) => {
  const out = { ...defaults };
  Object.entries(overrides || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim()) out[key] = value;
  });
  return out;
};

function initStudio(root) {
  if (root.dataset.stReady) return;
  root.dataset.stReady = 'true';

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => [...root.querySelectorAll(sel)];
  const config = JSON.parse($('[data-st-config]').textContent);
  const base = config.root.replace(/\/$/, '') + '/';
  const money = (cents) => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
  const count = (n) => Number(n).toLocaleString(config.locale);
  const text = withOverrides(DEFAULT_TEXT, config.text);
  const colors = withOverrides(DEFAULT_COLORS, config.colors);
  /* t('btn_add', { count: '500' }) fills {count}; unknown placeholders stay as written. */
  const t = (key, vars) => String(text[key] || '').replace(/\{(\w+)\}/g, (match, name) => (vars && Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match));

  const form = $('[data-st-form]');
  const fileInput = $('[data-st-file]');
  const canvas = $('[data-st-canvas]');
  const guides = $('[data-st-guides]');
  const mock = $('[data-st-mock]');
  const ctx = canvas.getContext('2d');
  const gctx = guides.getContext('2d');
  const mctx = mock.getContext('2d');
  [canvas, guides].forEach((c) => { c.width = SIZE; c.height = SIZE; });
  mock.width = 600; mock.height = 600;

  const startVariant = config.variants.find((v) => String(v.id) === $('[data-st-variant]').value) || config.variants[0];
  const state = {
    shape: startVariant.option1,
    quantity: startVariant.option2,
    image: null,
    file: null,
    isPdf: false,
    pdfNoPreview: false,
    reading: false,
    dpi: 0,
    zoom: 1,
    offX: 0,
    offY: 0,
    guides: true,
    approved: false,
    busy: false,
  };

  const variantFor = (shape, quantity) => config.variants.find((v) => v.option1 === shape && v.option2 === quantity);
  const current = () => variantFor(state.shape, state.quantity);

  /* ---------- drawing ---------- */
  const coverRect = (w, h) => {
    const s = Math.max(SIZE / w, SIZE / h) * state.zoom;
    const dw = w * s, dh = h * s;
    return { x: (SIZE - dw) / 2 + state.offX * SIZE, y: (SIZE - dh) / 2 + state.offY * SIZE, w: dw, h: dh };
  };

  function drawArtboard(c, forPrint) {
    c.save();
    c.clearRect(0, 0, SIZE, SIZE);
    c.fillStyle = '#FFFFFF';
    c.fillRect(0, 0, SIZE, SIZE);
    if (state.image) {
      const r = coverRect(state.image.width, state.image.height);
      c.drawImage(state.image, r.x, r.y, r.w, r.h);
    } else if (!forPrint) {
      c.fillStyle = colors.placeholderBg;
      c.fillRect(0, 0, SIZE, SIZE);
      c.fillStyle = colors.placeholderText;
      c.font = '600 46px "DM Sans", sans-serif';
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText(state.pdfNoPreview ? t('canvas_pdf_placeholder') : t('canvas_placeholder'), SIZE / 2, SIZE / 2);
    }
    c.restore();
  }

  function drawGuides() {
    gctx.clearRect(0, 0, SIZE, SIZE);
    if (!state.guides) return;
    const bleed = BLEED_MM * PX_PER_MM;
    const trim = TRIM_MM * PX_PER_MM;
    const safe = (TRIM_MM - 2 * SAFE_MM) * PX_PER_MM;
    const corner = trim * SQUARE_CORNER_RATIO;
    const safeCorner = Math.max(0, corner - SAFE_MM * PX_PER_MM);
    const cx = SIZE / 2, cy = SIZE / 2;
    const round = /^(round|redondo|rond|rund)$/i.test(state.shape);
    gctx.save();
    gctx.fillStyle = colors.outside;
    gctx.beginPath();
    gctx.rect(0, 0, SIZE, SIZE);
    if (round) gctx.arc(cx, cy, trim / 2, 0, Math.PI * 2, true);
    else gctx.roundRect(bleed, bleed, trim, trim, corner);
    gctx.fill('evenodd');
    gctx.lineWidth = 4;
    gctx.setLineDash([18, 12]);
    gctx.strokeStyle = colors.cut;
    gctx.beginPath();
    if (round) gctx.arc(cx, cy, trim / 2, 0, Math.PI * 2);
    else gctx.roundRect(bleed, bleed, trim, trim, corner);
    gctx.stroke();
    gctx.lineWidth = 2;
    gctx.setLineDash([4, 10]);
    gctx.strokeStyle = colors.safe;
    gctx.beginPath();
    if (round) gctx.arc(cx, cy, safe / 2, 0, Math.PI * 2);
    else gctx.roundRect((SIZE - safe) / 2, (SIZE - safe) / 2, safe, safe, safeCorner);
    gctx.stroke();
    gctx.restore();
  }

  function drawMock() {
    const bleed = BLEED_MM * PX_PER_MM;
    const trim = TRIM_MM * PX_PER_MM;
    mctx.clearRect(0, 0, mock.width, mock.height);
    mctx.drawImage(canvas, bleed, bleed, trim, trim, 0, 0, mock.width, mock.height);
    $('[data-st-mock-wrap]').classList.toggle('is-round', /^(round|redondo|rond|rund)$/i.test(state.shape));
  }

  function setStatus(el, text, tone) {
    el.textContent = text;
    el.className = 'hc-studio-status' + (tone ? ' ' + tone : '');
  }

  function checkResolution() {
    const res = $('[data-st-res]');
    if (!state.file) { setStatus(res, t('res_waiting')); state.dpi = 0; return; }
    if (state.isPdf) {
      if (state.pdfNoPreview) setStatus(res, t('res_pdf_manual'), 'warn');
      else setStatus(res, t('res_pdf_ok'), 'good');
      state.dpi = 300;
      return;
    }
    const r = coverRect(state.image.width, state.image.height);
    state.dpi = Math.floor((state.image.width / r.w) * DPI);
    if (state.dpi >= 300) setStatus(res, t('res_good', { dpi: state.dpi }), 'good');
    else if (state.dpi >= 150) setStatus(res, t('res_ok', { dpi: state.dpi }), 'warn');
    else setStatus(res, t('res_low', { dpi: state.dpi }), 'bad');
  }

  function render() {
    drawArtboard(ctx, false);
    drawGuides();
    drawMock();
    checkResolution();
    updateUi();
  }

  /* ---------- state of the order ---------- */
  const tooLow = () => Boolean(state.file) && !state.isPdf && state.dpi < 150;

  function updateUi() {
    const v = current();
    if (v) $('[data-st-variant]').value = v.id;
    root.dataset.shape = /^(round|redondo|rond|rund)$/i.test(state.shape) ? 'round' : 'square';
    const units = Number(state.quantity);
    const baseline = variantFor(state.shape, '100');

    if (v) {
      $('[data-st-total]').textContent = money(v.price);
      $('[data-st-unit]').textContent = money(v.price / units);
      $('[data-st-selection]').textContent = t('selection', { count: count(units), shape: state.shape.toLowerCase() });
    }
    $$('[data-st-pack]').forEach((button) => {
      const pv = variantFor(state.shape, button.dataset.stPack);
      button.setAttribute('aria-pressed', String(button.dataset.stPack === state.quantity));
      button.disabled = !pv || !pv.available;
      if (!pv) return;
      button.querySelector('[data-st-pack-total]').textContent = money(pv.price);
      button.querySelector('[data-st-pack-unit]').textContent = t('pack_each', { price: money(pv.price / Number(pv.option2)) });
      const saving = baseline ? Math.max(0, Math.floor((1 - (pv.price / Number(pv.option2)) / (baseline.price / 100)) * 100)) : 0;
      const badge = button.querySelector('[data-st-pack-saving]');
      badge.hidden = saving <= 0;
      badge.textContent = t('pack_save', { saving });
    });
    const select = $('[data-st-qty]');
    select.value = [...select.options].some((o) => o.value === state.quantity) ? state.quantity : '';

    const hasFile = Boolean(state.file);
    const approve = $('[data-st-approve]');
    approve.disabled = !hasFile || state.reading || tooLow();
    approve.checked = state.approved;
    $('[data-st-approve-wrap]').classList.toggle('is-waiting', approve.disabled);
    $('[data-st-adjust]').hidden = !state.image;
    $('[data-st-tip]').hidden = !state.image;
    $('[data-st-drop]').classList.toggle('has-file', hasFile);
    $('[data-st-file-cta]').textContent = hasFile ? t('file_cta_replace') : t('file_cta');
    $('[data-st-step="artwork"]').dataset.done = String(hasFile && !tooLow() && !state.reading);
    $('[data-st-step="approve"]').dataset.done = String(state.approved);

    const label = $('[data-st-label]');
    const next = $('[data-st-next]');
    const submit = $('[data-st-submit]');
    submit.disabled = state.busy || state.reading || !v || !v.available;
    if (!v || !v.available) { label.textContent = t('btn_unavailable'); next.textContent = t('next_unavailable'); }
    else if (state.busy) { label.textContent = t('btn_busy'); next.textContent = t('next_busy'); }
    else if (state.reading) { label.textContent = t('btn_reading'); next.textContent = t('next_reading'); }
    else if (!hasFile) { label.textContent = t('btn_upload'); next.textContent = t('next_upload'); }
    else if (tooLow()) { label.textContent = t('btn_sharper'); next.textContent = t('next_sharper'); }
    else if (!state.approved) { label.textContent = t('btn_approve'); next.textContent = t('next_approve'); }
    else { label.textContent = t('btn_add', { count: count(units) }); next.textContent = t('next_ready', { price: money(v.price), count: count(units), shape: state.shape.toLowerCase() }); }
  }

  const resetApproval = () => { state.approved = false; };

  /* ---------- file handling ---------- */
  const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  async function renderPdf(file) {
    const pdfjs = await import(PDFJS);
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
    const page = await pdf.getPage(1);
    const vp1 = page.getViewport({ scale: 1 });
    const scale = (SIZE * 1.05) / Math.min(vp1.width, vp1.height);
    const vp = page.getViewport({ scale });
    const c = document.createElement('canvas');
    c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
    await page.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
    return c;
  }

  async function handleFile(f) {
    const status = $('[data-st-file-status]');
    $('[data-st-error]').hidden = true;
    if (!f) return;
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (f.size > MAX_BYTES) { setStatus(status, t('status_too_large'), 'bad'); return; }
    if (!isPdf && !/^image\/(png|jpeg)$/.test(f.type)) { setStatus(status, t('status_wrong_type'), 'bad'); return; }
    resetApproval();
    state.reading = true;
    setStatus(status, t('status_reading', { file: f.name }));
    updateUi();
    try {
      state.image = isPdf ? await renderPdf(f) : await loadImage(URL.createObjectURL(f));
      state.pdfNoPreview = false;
      setStatus(status, f.name, 'good');
    } catch (err) {
      console.warn('[hc-studio] file', err);
      if (isPdf) {
        state.image = null;
        state.pdfNoPreview = true;
        setStatus(status, t('status_pdf_no_preview', { file: f.name }), 'warn');
      } else {
        state.reading = false;
        setStatus(status, t('status_unreadable'), 'bad');
        updateUi();
        return;
      }
    }
    state.file = f;
    state.isPdf = isPdf;
    state.zoom = 1; state.offX = 0; state.offY = 0;
    $('[data-st-zoom]').value = 100;
    state.reading = false;
    render();
  }

  fileInput.addEventListener('change', () => handleFile(fileInput.files && fileInput.files[0]));
  const drop = $('[data-st-drop]');
  ['dragenter', 'dragover'].forEach((type) => drop.addEventListener(type, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
  ['dragleave', 'drop'].forEach((type) => drop.addEventListener(type, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
  drop.addEventListener('drop', (e) => {
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  });

  /* ---------- controls ---------- */
  $$('[data-st-shape]').forEach((input) => input.addEventListener('change', () => {
    state.shape = input.value;
    resetApproval();
    render();
  }));
  $$('[data-st-pack]').forEach((button) => button.addEventListener('click', () => {
    state.quantity = button.dataset.stPack;
    updateUi();
  }));
  $('[data-st-qty]').addEventListener('change', (e) => {
    if (!e.target.value) return;
    state.quantity = e.target.value;
    updateUi();
  });
  $('[data-st-zoom]').addEventListener('input', (e) => {
    state.zoom = Number(e.target.value) / 100;
    resetApproval();
    render();
  });
  $('[data-st-center]').addEventListener('click', () => {
    state.offX = 0; state.offY = 0;
    resetApproval();
    render();
  });
  $('[data-st-guides-toggle]').addEventListener('change', (e) => { state.guides = e.target.checked; drawGuides(); });
  $('[data-st-approve]').addEventListener('change', (e) => {
    state.approved = e.target.checked;
    $('[data-st-approve-wrap]').classList.remove('is-needed');
    updateUi();
  });
  $$('[data-st-view]').forEach((button) => button.addEventListener('click', () => {
    $$('[data-st-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    $$('[data-st-panel]').forEach((panel) => { panel.hidden = panel.dataset.stPanel !== button.dataset.stView; });
  }));

  /* ---------- move the artwork ---------- */
  let drag = null;
  const toCanvas = (ev) => {
    const r = guides.getBoundingClientRect();
    return { x: ((ev.clientX - r.left) / r.width) * SIZE, y: ((ev.clientY - r.top) / r.height) * SIZE };
  };
  guides.addEventListener('pointerdown', (ev) => {
    if (!state.image) return;
    drag = { start: toCanvas(ev), ox: state.offX, oy: state.offY };
    guides.setPointerCapture(ev.pointerId);
    guides.classList.add('is-dragging');
  });
  guides.addEventListener('pointermove', (ev) => {
    if (!drag) return;
    const p = toCanvas(ev);
    state.offX = drag.ox + (p.x - drag.start.x) / SIZE;
    state.offY = drag.oy + (p.y - drag.start.y) / SIZE;
    resetApproval();
    render();
  });
  const endDrag = () => { drag = null; guides.classList.remove('is-dragging'); };
  guides.addEventListener('pointerup', endDrag);
  guides.addEventListener('pointercancel', endDrag);
  guides.addEventListener('keydown', (ev) => {
    if (!state.image) return;
    const step = ev.shiftKey ? 0.05 : 0.01;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const m = moves[ev.key];
    if (!m) return;
    ev.preventDefault();
    state.offX += m[0]; state.offY += m[1];
    resetApproval();
    render();
  });

  /* ---------- print file and cart ---------- */
  async function buildPrintFile() {
    if (!state.image) return null;
    const out = document.createElement('canvas');
    out.width = SIZE; out.height = SIZE;
    drawArtboard(out.getContext('2d'), true);
    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const handle = (root.dataset.handle || 'coaster').slice(0, 40);
    return new File([blob], `${handle}-${state.shape.toLowerCase()}-print-99mm-300dpi.png`, { type: 'image/png' });
  }

  function guide() {
    if (!state.file || tooLow()) {
      fileInput.click();
      return;
    }
    const wrap = $('[data-st-approve-wrap]');
    wrap.classList.add('is-needed');
    wrap.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    $('[data-st-approve]').focus({ preventScroll: true });
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const v = current();
    if (state.busy || state.reading || !v || !v.available) return;
    if (!state.file || tooLow() || !state.approved) { guide(); return; }
    state.busy = true;
    updateUi();
    const error = $('[data-st-error]');
    error.hidden = true;
    $('[data-st-success]').hidden = true;
    const data = new FormData();
    data.append('id', String(v.id));
    data.append('quantity', '1');
    data.append(`properties[${t('property_artwork')}]`, state.file, state.file.name);
    const notes = ($('[data-st-notes]').value || '').trim();
    if (notes) data.append(`properties[${t('property_notes')}]`, notes);
    data.append('properties[_Creation method]', 'Customer artwork');
    data.append('properties[_Artwork approved]', 'Yes, approved by customer');
    try {
      const print = await buildPrintFile();
      if (print) {
        data.append('properties[_Print file]', print, print.name);
        data.append('properties[_Print file spec]', `${TRIM_MM} mm ${state.shape.toLowerCase()}, ${BLEED_MM} mm bleed, ${SIZE} x ${SIZE} px, 300 DPI`);
      }
    } catch (e) {
      console.warn('[hc-studio] print file', e);
    }
    const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))];
    if (ids.length) data.append('sections', ids.join(','));
    data.append('sections_url', location.pathname + location.search);
    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(v.id), quantity: 1 }], promise: deferred.promise }));
    try {
      const response = await fetch(base + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || t('error_add'));
      const cartResponse = await fetch(base + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) {
        deferred.resolve({ detail: { didError: true } });
        location.assign(config.cartUrl);
        return;
      }
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'product-form-component', sourceId: form.id, productId: config.productId, itemCount: 1, items: cart.items, sections: result.sections } });
      $('[data-st-success]').hidden = false;
      deferred.promise.then(openCartDrawer).catch(() => {});
    } catch (e) {
      deferred.resolve({ detail: { didError: true } });
      error.textContent = e.message || t('error_add');
      error.hidden = false;
      error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      state.busy = false;
      updateUi();
    }
  });

  render();
}

document.querySelectorAll('[data-hc-studio]').forEach(initStudio);
document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-hc-studio]').forEach(initStudio));
